import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Stack,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Refresh,
  Error,
  CheckCircle,
  Pending,
  Schedule,
  Search,
  Sort,
  FilterList,
  SelectAll,
  ClearAll as ClearAllIcon,
  Send
} from '@mui/icons-material';
import { fetchEMRMeasurements } from '../services/ApiService';

interface Device {
  id: number;
  model: string;
  manufacturer: string;
  name: string;
  version: string;
  platform: string;
  idiom: string;
  deviceType: string;
  appVersion: string;
}

interface EMRAudit {
  id: number;
  measurementId: number;
  fhirResourceType: string | null;
  fhirResourceId: string | null;
  loincCode: string | null;
  observationDisplayName: string | null;
  fhirRequestBundle: string | null;
  requestSentOn: string | null;
  fhirEndpoint: string | null;
  fhirResponseBundle: string | null;
  httpStatusCode: number | null;
  responseReceivedOn: string | null;
  fhirResponseStatus: string | null;
  fhirVersion: string | null;
  emrSystem: string;
  emrEnvironment: string;
  tenantId: string | null;
  patientFHIRId: string | null;
  patientMRN: string | null;
  integrationStatus: string;
  retryCount: number;
  nextRetryOn: string | null;
  errorMessage: string | null;
  errorDetails: string | null;
  operationOutcome: string | null;
  createdOn: string;
  updatedOn: string | null;
  lastUpdated: string | null;
  measurement: any | null;
}

interface BloodGlucoseReading {
  date?: string;
  value?: number;
  userId?: number;
  unit?: string;
  controlled?: boolean;
}

interface BloodPressureReading {
  date?: string;
  systolic?: number;
  diastolic?: number;
  userId?: number;
  unit?: string;
}

interface ReadingData {
  date?: string;
  value?: number;
  systolic?: number;
  diastolic?: number;
  pulseRate?: number;
  spo2?: number;
  weight?: number;
  height?: number;
  bmi?: number;
  temperature?: number;
  respiratoryRate?: number;
  userId?: number;
  unit?: string;
  controlled?: boolean;
  rawReading: string;
  measurementType: string;
}

export interface Measurement {
  id: number;
  measurementType: string;
  reading: string;
  unit: string | null;
  providerCode: string;
  readingOn: string;
  updatedOn: string;
  isManualEntry: boolean;
  isDeleted: boolean;
  userId: string;
  deviceModel: string;
  device: Device;
  userName: string;
  isBreach: boolean;
  isAcknowledged: boolean;
  breachValues: any | null;
  emrAudit: EMRAudit | null;
}

interface Props {
  loggedInUser: any;
  groupcode: string;
  onSubmitReading: (measurement: Measurement) => Promise<void>;
  onSubmitMultipleReadings: (measurements: Measurement[]) => Promise<void>;
  refreshInterval?: number;
}

const MeasurementDisplay: React.FC<Props> = ({
  loggedInUser,
  groupcode,
  onSubmitReading,
  onSubmitMultipleReadings,
  refreshInterval = 0
}) => {
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<{ [key: number]: boolean }>({});
  const [submittingMultiple, setSubmittingMultiple] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [measurementTypeFilter, setMeasurementTypeFilter] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(12);
  const [selectedMeasurements, setSelectedMeasurements] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);

  const fetchMeasurements = async (preserveSelections: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchEMRMeasurements(loggedInUser, groupcode);
      setMeasurements(data);
      setLastUpdated(new Date());
      setCurrentPage(1);

      if (!preserveSelections) {
        setSelectedMeasurements([]);
        setSelectAll(false);
      }
    } catch (err) {
      console.error('Error fetching measurements:');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeasurements();

    let intervalId: NodeJS.Timeout | null = null;
    if (refreshInterval > 0) {
      intervalId = setInterval(() => fetchMeasurements(true), refreshInterval);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [loggedInUser, groupcode, refreshInterval]);

  const measurementTypes = useMemo(() => {
    const types = measurements.map(m => m.measurementType);
    return ['all', ...Array.from(new Set(types)).sort()];
  }, [measurements]);

  const filteredAndSortedMeasurements = useMemo(() => {
    let filtered = measurements;

    if (searchTerm) {
      filtered = filtered.filter(measurement =>
        measurement.userName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (measurementTypeFilter !== 'all') {
      filtered = filtered.filter(measurement =>
        measurement.measurementType === measurementTypeFilter
      );
    }

    return filtered.sort((a, b) => {
      const dateA = new Date(a.readingOn).getTime();
      const dateB = new Date(b.readingOn).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [measurements, searchTerm, measurementTypeFilter, sortOrder]);

  const totalPages = Math.ceil(filteredAndSortedMeasurements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMeasurements = filteredAndSortedMeasurements.slice(startIndex, endIndex);

  useEffect(() => {
    const selectableMeasurements = filteredAndSortedMeasurements.filter(canSubmitMeasurement);
    const allSelected = selectableMeasurements.length > 0 &&
      selectableMeasurements.every(m => selectedMeasurements.includes(m.id));
    setSelectAll(allSelected);
  }, [selectedMeasurements, filteredAndSortedMeasurements]);

  const patientNames = useMemo(() => {
    const names = measurements.map(m => m.userName);
    return Array.from(new Set(names)).sort();
  }, [measurements]);

  const parseReading = (readingJson: string, measurementType: string): ReadingData => {
    try {
      const parsed = JSON.parse(readingJson);
      const readingData: ReadingData = {
        ...parsed,
        rawReading: readingJson,
        measurementType
      };
      return readingData;
    } catch (error) {
      return {
        rawReading: readingJson,
        measurementType
      };
    }
  };

  const handleSubmit = async (measurement: Measurement) => {
    setSubmitting(prev => ({ ...prev, [measurement.id]: true }));
    try {
      await onSubmitReading(measurement);
      setTimeout(() => fetchMeasurements(true), 1000);
    } catch (error) {
      console.error('Failed to submit reading:', error);
    } finally {
      setSubmitting(prev => ({ ...prev, [measurement.id]: false }));
    }
  };

  const handleSubmitMultiple = async () => {
    setSubmittingMultiple(true);
    try {
      const selectedMeasurementsData = measurements.filter(m =>
        selectedMeasurements.includes(m.id) && canSubmitMeasurement(m)
      );

      if (selectedMeasurementsData.length === 0) {
        alert('No selectable measurements found. Some may already be submitted.');
        return;
      }

      await onSubmitMultipleReadings(selectedMeasurementsData);
      setConfirmDialogOpen(false);

      setTimeout(() => {
        fetchMeasurements(true);
        setSelectedMeasurements(prev =>
          prev.filter(id => !selectedMeasurementsData.some(m => m.id === id))
        );
      }, 1000);
    } catch (error) {
      console.error('Failed to submit multiple readings:');
    } finally {
      setSubmittingMultiple(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString + "Z").toLocaleString();
  };

  const formatAuditDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString + "Z").toLocaleString();
  };

  const handleRefresh = () => {
    fetchMeasurements(false);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (event: any) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleSelectMeasurement = (measurementId: number) => {
    setSelectedMeasurements(prev =>
      prev.includes(measurementId)
        ? prev.filter(id => id !== measurementId)
        : [...prev, measurementId]
    );
  };

  const handleSelectAll = () => {
    const selectableMeasurements = filteredAndSortedMeasurements.filter(canSubmitMeasurement);

    if (selectAll) {
      setSelectedMeasurements(prev =>
        prev.filter(id => !selectableMeasurements.some(m => m.id === id))
      );
    } else {
      const selectableIds = selectableMeasurements.map(m => m.id);
      setSelectedMeasurements(prev =>
        Array.from(new Set([...prev, ...selectableIds]))
      );
    }
  };

  const canSubmitMeasurement = (measurement: Measurement): boolean => {
    if (!measurement.emrAudit || measurement.emrAudit.id === 0) {
      return true;
    }

    const audit = measurement.emrAudit;
    const status = audit.integrationStatus?.toLowerCase();

    const allowResubmissionStatuses = [
      'pending',
      'failed',
      'error',
      'rejected',
      'retry',
      'retrying',
      'not_started',
      'not_submitted',
      'queued'
    ];

    const preventSubmissionStatuses = [
      'completed',
      'success',
      'submitted',
      'accepted',
      'in_progress',
      'processing'
    ];

    if (status && preventSubmissionStatuses.includes(status)) {
      return false;
    }

    return !status || allowResubmissionStatuses.includes(status);
  };

  const getAuditStatusInfo = (audit: EMRAudit | null) => {
    if (!audit || audit.id === 0) {
      return {
        text: 'Not Submitted',
        color: 'default' as const,
        icon: <Pending fontSize="small" />,
        canSubmit: true
      };
    }

    const status = audit.integrationStatus?.toLowerCase();

    switch (status) {
      case 'completed':
      case 'success':
      case 'submitted':
        return {
          text: 'Submitted to EMR',
          color: 'success' as const,
          icon: <CheckCircle fontSize="small" />,
          canSubmit: false
        };

      case 'pending':
      case 'in_progress':
      case 'processing':
        return {
          text: 'Submission in Progress',
          color: 'warning' as const,
          icon: <Schedule fontSize="small" />,
          canSubmit: false
        };

      case 'failed':
      case 'error':
      case 'rejected':
        return {
          text: 'Submission Failed',
          color: 'error' as const,
          icon: <Error fontSize="small" />,
          canSubmit: true
        };

      case 'retry':
      case 'retrying':
        return {
          text: 'Retrying Submission',
          color: 'info' as const,
          icon: <Refresh fontSize="small" />,
          canSubmit: true
        };

      default:
        return {
          text: audit.integrationStatus || 'Unknown',
          color: 'default' as const,
          icon: <Pending fontSize="small" />,
          canSubmit: true
        };
    }
  };

  const renderReadingValue = (measurement: Measurement, readingData: ReadingData) => {
    const { measurementType } = readingData;

    switch (measurementType.toLowerCase()) {
      case 'blood pressure':
      case 'bp':
        return (
          <Box>
            <Typography variant="body2">
              <strong>Blood Pressure:</strong>{' '}
              <Typography component="span" variant="body2" color="primary" fontWeight="bold">
                {readingData.systolic}/{readingData.diastolic} {readingData.unit || measurement.unit || 'mmHg'}
              </Typography>
            </Typography>
            {readingData.systolic && readingData.diastolic && (
              <Typography variant="caption" color="text.secondary">
                Systolic: {readingData.systolic} | Diastolic: {readingData.diastolic}
              </Typography>
            )}
          </Box>
        );

      case 'blood glucose':
      case 'glucose':
      case 'blood sugar':
        return (
          <Typography variant="body2">
            <strong>Glucose:</strong>{' '}
            <Typography component="span" variant="body2" color="primary" fontWeight="bold">
              {readingData.value} {readingData.unit || measurement.unit || 'mg/dL'}
            </Typography>
          </Typography>
        );

      case 'pulse rate':
      case 'heart rate':
        return (
          <Typography variant="body2">
            <strong>Pulse Rate:</strong>{' '}
            <Typography component="span" variant="body2" color="primary" fontWeight="bold">
              {readingData.value || readingData.pulseRate} {readingData.unit || measurement.unit || 'bpm'}
            </Typography>
          </Typography>
        );

      case 'spo2':
      case 'oxygen saturation':
        return (
          <Typography variant="body2">
            <strong>SpO₂:</strong>{' '}
            <Typography component="span" variant="body2" color="primary" fontWeight="bold">
              {readingData.value || readingData.spo2} {readingData.unit || measurement.unit || '%'}
            </Typography>
          </Typography>
        );

      case 'weight':
        return (
          <Typography variant="body2">
            <strong>Weight:</strong>{' '}
            <Typography component="span" variant="body2" color="primary" fontWeight="bold">
              {readingData.value || readingData.weight} {readingData.unit || measurement.unit || 'kg'}
            </Typography>
          </Typography>
        );

      case 'height':
        return (
          <Typography variant="body2">
            <strong>Height:</strong>{' '}
            <Typography component="span" variant="body2" color="primary" fontWeight="bold">
              {readingData.value || readingData.height} {readingData.unit || measurement.unit || 'cm'}
            </Typography>
          </Typography>
        );

      case 'bmi':
      case 'body mass index':
        return (
          <Typography variant="body2">
            <strong>BMI:</strong>{' '}
            <Typography component="span" variant="body2" color="primary" fontWeight="bold">
              {readingData.value || readingData.bmi} {readingData.unit || measurement.unit || 'kg/m²'}
            </Typography>
          </Typography>
        );

      case 'temperature':
        return (
          <Typography variant="body2">
            <strong>Temperature:</strong>{' '}
            <Typography component="span" variant="body2" color="primary" fontWeight="bold">
              {readingData.value || readingData.temperature} {readingData.unit || measurement.unit || '°C'}
            </Typography>
          </Typography>
        );

      case 'respiratory rate':
      case 'respiration rate':
        return (
          <Typography variant="body2">
            <strong>Respiratory Rate:</strong>{' '}
            <Typography component="span" variant="body2" color="primary" fontWeight="bold">
              {readingData.value || readingData.respiratoryRate} {readingData.unit || measurement.unit || 'breaths/min'}
            </Typography>
          </Typography>
        );

      default:
        if (readingData.value !== undefined) {
          return (
            <Typography variant="body2">
              <strong>{measurement.measurementType}:</strong>{' '}
              <Typography component="span" variant="body2" color="primary" fontWeight="bold">
                {readingData.value} {readingData.unit || measurement.unit || ''}
              </Typography>
            </Typography>
          );
        } else {
          return (
            <Typography variant="body2">
              <strong>Reading:</strong>{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                {measurement.reading.substring(0, 50)}...
              </Typography>
            </Typography>
          );
        }
    }
  };

  const renderControlledStatus = (readingData: ReadingData) => {
    if (readingData.controlled !== undefined) {
      return (
        <Typography variant="body2">
          <strong>Controlled:</strong> {readingData.controlled ? 'Yes' : 'No'}
        </Typography>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="300px">
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography color="text.secondary">
            Loading measurements...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          <Typography >Error Loading Measurements</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Container>
    );
  }

  if (measurements.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom color="text.secondary">
            No Measurements Found
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            No measurement data available for group {groupcode}.
          </Typography>
          <Button variant="contained" onClick={handleRefresh} startIcon={<Refresh />}>
            Refresh
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <div style={{ display: "flex", alignItems: 'flex-start', justifyContent: "flex-start", flexDirection: 'column' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Patient Measurements
            </Typography>
            <Typography sx={{ fontWeight: 'bold', textDecoration: 'underline' }} variant="subtitle1" color="text.secondary">
              Group: {groupcode}
            </Typography>
          </div>

          <Box display="flex" alignItems="center" gap={2}>
            {lastUpdated && (
              <Typography variant="body2" color="text.secondary">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </Typography>
            )}
            <Tooltip title="Refresh measurements">
              <IconButton onClick={handleRefresh} color="primary">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {selectedMeasurements.length > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1, color: 'white' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">
                {selectedMeasurements.length} measurement(s) selected
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleSelectAll}
                  startIcon={selectAll ? <ClearAllIcon /> : <SelectAll />}
                >
                  {selectAll ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => setConfirmDialogOpen(true)}
                  startIcon={<Send />}
                  disabled={submittingMultiple}
                >
                  {submittingMultiple ? 'Submitting...' : `Submit Selected (${selectedMeasurements.length})`}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ mt: 3, mb: 2 }}>
          <TextField
            placeholder="Search by patient name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
            size="small"
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{
              backgroundColor: 'transparent',
              padding: '0 4px',
              '&.MuiInputLabel-shrink': {
                backgroundColor: 'transparent',
              }
            }}>Measurement Type</InputLabel>
            <Select
              value={measurementTypeFilter}
              onChange={(e) => setMeasurementTypeFilter(e.target.value)}
              label="Measurement Type"
              notched sx={{
                '& .MuiSelect-select': {
                  paddingTop: '16px',
                  paddingBottom: '8px',
                  fontSize: '0.8rem', // Smaller select font
                  minHeight: 'auto'
                }
              }}
            >
              {measurementTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type === 'all' ? 'All Types' : type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{
              backgroundColor: 'transparent',
              padding: '0 4px',
              '&.MuiInputLabel-shrink': {
                backgroundColor: 'transparent',
              }
            }}>Sort by</InputLabel>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              label="Sort by"
              notched sx={{
                '& .MuiSelect-select': {
                  paddingTop: '16px',
                  paddingBottom: '8px',
                  fontSize: '0.8rem', // Smaller select font
                  minHeight: 'auto'
                }
              }}
            >
              <MenuItem value="desc">Newest First</MenuItem>
              <MenuItem value="asc">Oldest First</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{
              backgroundColor: 'transparent',
              padding: '0 8px',
              '&.MuiInputLabel-shrink': {
                backgroundColor: 'transparent',
              }
            }}>Items per page</InputLabel>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              label="Items per page"
              notched sx={{
                '& .MuiSelect-select': {
                  paddingTop: '16px',
                  paddingBottom: '8px',
                  fontSize: '0.8rem', // Smaller select font
                  minHeight: 'auto'
                }
              }}
            >
              <MenuItem value={6}>6</MenuItem>
              <MenuItem value={12}>12</MenuItem>
              <MenuItem value={24}>24</MenuItem>
              <MenuItem value={48}>48</MenuItem>
            </Select>
          </FormControl>


          {(searchTerm || measurementTypeFilter !== 'all') && (
            <Chip
              label={`${filteredAndSortedMeasurements.length} results`}
              color="primary"
              variant="outlined"
              onDelete={() => {
                setSearchTerm('');
                setMeasurementTypeFilter('all');
              }}
            />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedMeasurements.length)} of{' '}
          {filteredAndSortedMeasurements.length} measurements
          {searchTerm && ` matching "${searchTerm}"`}
          {measurementTypeFilter !== 'all' && ` of type "${measurementTypeFilter}"`}
        </Typography>
      </Box>

      {paginatedMeasurements.length > 0 ? (
        <Grid container spacing={3}>
          {paginatedMeasurements.map((measurement) => {
            const readingData = parseReading(measurement.reading, measurement.measurementType);
            const canSubmit = canSubmitMeasurement(measurement);
            const auditStatus = getAuditStatusInfo(measurement.emrAudit);
            const isSelected = selectedMeasurements.includes(measurement.id);

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={measurement.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    border: isSelected ? 2 : 1,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, position: 'relative' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectMeasurement(measurement.id)}
                          disabled={!canSubmit}
                          sx={{ position: 'absolute' }}
                        />
                      }
                      label=""
                    />

                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} sx={{ pr: 4 }}>
                      <Typography component="h3">
                        {measurement.measurementType}
                      </Typography>
                      <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                        <Chip
                          label={measurement.isBreach ? 'Breach' : 'Normal'}
                          color={measurement.isBreach ? 'error' : 'success'}
                          size="small"
                        />
                        <Tooltip title={
                          measurement.emrAudit?.errorMessage ?
                            `Error: ${measurement.emrAudit.errorMessage}` :
                            auditStatus.text
                        }>
                          <Chip
                            icon={auditStatus.icon}
                            label={auditStatus.text}
                            color={auditStatus.color}
                            size="small"
                            variant="outlined"
                            sx={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ '& > *:not(:last-child)': { mb: 1 } }}>
                      {renderReadingValue(measurement, readingData)}

                      <Typography variant="body2">
                        <strong>Date:</strong> {formatDate(measurement.readingOn)}
                      </Typography>

                      {renderControlledStatus(readingData)}

                      {measurement.emrAudit && measurement.emrAudit.id > 0 && (
                        <>
                          <Typography variant="body2">
                            <strong>EMR Status:</strong>{' '}
                            <Typography component="span" variant="body2" color="text.secondary">
                              {measurement.emrAudit.integrationStatus}
                            </Typography>
                          </Typography>

                          {measurement.emrAudit.errorMessage && (
                            <Alert severity="error" sx={{ py: 0.5, mt: 1 }}>
                              <Typography variant="caption">
                                {measurement.emrAudit.errorMessage}
                              </Typography>
                            </Alert>
                          )}

                          {measurement.emrAudit.requestSentOn && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Sent: {formatAuditDate(measurement.emrAudit.requestSentOn)}
                            </Typography>
                          )}
                        </>
                      )}

                      <Typography variant="body2">
                        <strong>Device:</strong> {measurement.device.manufacturer} {measurement.device.name}
                      </Typography>

                      <Typography variant="body2">
                        <strong>Patient:</strong>{' '}
                        <Typography component="span" variant="body2" color="secondary.main" fontWeight="medium">
                          {measurement.userName}
                        </Typography>
                      </Typography>

                      <Typography variant="body2">
                        <strong>Type:</strong> {measurement.isManualEntry ? 'Manual' : 'Automatic'}
                      </Typography>
                    </Box>
                  </CardContent>

                  {canSubmit && (
                    <CardActions>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleSubmit(measurement)}
                        disabled={submitting[measurement.id] || !canSubmitMeasurement(measurement)}
                        startIcon={
                          submitting[measurement.id] ?
                            <CircularProgress size={16} /> :
                            null
                        }
                        sx={{
                          backgroundColor: auditStatus.canSubmit ? 'primary.main' : 'grey.400',
                          '&:hover': {
                            backgroundColor: auditStatus.canSubmit ? 'primary.dark' : 'grey.400'
                          }
                        }}
                      >
                        {submitting[measurement.id] ? 'Submitting...' :
                          auditStatus.canSubmit ? 'Submit to EMR' : 'Already Submitted'}
                      </Button>
                    </CardActions>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography gutterBottom>
            No measurements found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm || measurementTypeFilter !== 'all'
              ? `No measurements match your ${searchTerm ? 'search' : ''}${searchTerm && measurementTypeFilter !== 'all' ? ' and ' : ''}${measurementTypeFilter !== 'all' ? 'filter' : ''}`
              : 'No measurements available'
            }
          </Typography>
          {(searchTerm || measurementTypeFilter !== 'all') && (
            <Button variant="outlined" onClick={() => {
              setSearchTerm('');
              setMeasurementTypeFilter('all');
            }}>
              Clear filters
            </Button>
          )}
        </Paper>
      )}

      {filteredAndSortedMeasurements.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {totalPages}
            </Typography>
          </Stack>
        </Box>
      )}

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Selected Measurements</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to submit {selectedMeasurements.length} measurement(s) to EMR?
          </Typography>

          {selectedMeasurements.some(id => {
            const m = measurements.find(m => m.id === id);
            return m && !canSubmitMeasurement(m);
          }) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Some selected measurements may already be submitted and will be skipped.
              </Alert>
            )}

          <List dense>
            {selectedMeasurements.slice(0, 5).map((id, index) => {
              const measurement = measurements.find(m => m.id === id);
              const canSubmit = measurement ? canSubmitMeasurement(measurement) : false;
              return measurement ? (
                <ListItem key={id}>
                  <ListItemIcon>
                    {canSubmit ? <CheckCircle color="primary" /> : <Error color="warning" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={`${measurement.measurementType} - ${measurement.userName}`}
                    secondary={
                      <>
                        {formatDate(measurement.readingOn)}
                        {!canSubmit && (
                          <Typography variant="caption" color="warning.main" display="block">
                            Already submitted
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ) : null;
            })}
            {selectedMeasurements.length > 5 && (
              <ListItem>
                <ListItemText
                  primary={`...and ${selectedMeasurements.length - 5} more`}
                  primaryTypographyProps={{ color: 'text.secondary' }}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={submittingMultiple}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitMultiple}
            variant="contained"
            disabled={submittingMultiple}
            startIcon={submittingMultiple ? <CircularProgress size={16} /> : <Send />}
          >
            {submittingMultiple ? 'Submitting...' : 'Submit All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MeasurementDisplay;